import type { ApiSchemas } from '@rental/contracts/client'
import type { PrivacyHold, StaffLocale } from '@rental/domain'
import { format, formatDateTime, plural, type StaffMessages } from '@rental/i18n'

/*
 * S8: Sistema's "Conservazione dei dati" in words. The numbers are the
 * server's, read by the job's own queries; the screen only says them. A
 * period that is off is said to be off, never as a number of days: null
 * means nothing of that class goes by itself.
 */

type Privacy = ApiSchemas['SystemPrivacy']
export type PeriodName = keyof Privacy['periods']

/** The settings, in the order the RUNBOOK and the CLI give them. */
export const PERIOD_NAMES = [
  'identityDocuments',
  'vehiclePhotos',
  'fineWindow',
  'customer',
] as const satisfies readonly PeriodName[]

/** One period: its days and what they count from, or what being off means. */
export function periodWords(
  name: PeriodName,
  days: number | null,
  locale: StaffLocale,
  m: StaffMessages,
): string {
  const copy = m.system.privacy
  return days === null
    ? copy.off[name]
    : format(copy.after[name], { days: plural(locale, copy.days, days) })
}

/** Every period off: nothing goes by itself, only what a request erases. */
export const allOff = (periods: Privacy['periods']): boolean =>
  PERIOD_NAMES.every((name) => periods[name] === null)

/** What keeps the held ones: each hold by name, with how many it keeps. */
export function holdsWords(
  holds: readonly { hold: PrivacyHold; count: number }[],
  m: StaffMessages,
): string {
  const copy = m.system.privacy.due
  return holds.length === 0
    ? copy.none
    : holds
        .map(({ hold, count }) => format(copy.hold, { hold: m.privacy.holds[hold], count }))
        .join(', ')
}

/**
 * What is erased but not finished: a count with its oldest one's date, or
 * none. `count` null: not watched (backup copies, with backups off).
 */
export function unfinishedWords(
  count: number | null,
  oldest: string | null,
  locale: StaffLocale,
  m: StaffMessages,
): string {
  const copy = m.system.privacy.unfinished
  if (count === null) return copy.backupsOff
  if (count === 0) return copy.none
  return oldest === null
    ? String(count)
    : format(copy.since, { count, when: formatDateTime(locale, oldest) })
}
